// =============================================================================
// ProductImagesManager — inline image management for a single product variant.
// =============================================================================
// The product equivalent of ModelImagesManager: upload photos straight to
// object storage (MinIO in dev, DigitalOcean Spaces in prod) via a presigned
// PUT URL, then remove them. The first image added becomes the primary.
// Reused by the standalone ProductImagesModal and embedded in the product edit
// modal so both manage images the exact same way.

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, getApiError } from '../../lib/api';
import { uploadProductImage } from '../../lib/upload';

interface ProductImage {
  id: string;
  url: string;
  alt: string | null;
  isPrimary: boolean;
  displayOrder: number;
}
interface ProductDetail {
  id: string;
  name: string;
  images: ProductImage[];
}

export function ProductImagesManager({ productId }: { productId: string }): JSX.Element {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => apiGet<ProductDetail>(`/products/${productId}`),
  });
  const images = product.data?.images ?? [];

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['product', productId] });
    void qc.invalidateQueries({ queryKey: ['products-admin'] });
    void qc.invalidateQueries({ queryKey: ['products'] });
  };

  const register = useMutation({
    mutationFn: (publicUrl: string) =>
      apiPost(`/products/${productId}/images`, { url: publicUrl, isPrimary: images.length === 0 }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (imageId: string) => apiDelete(`/products/${productId}/images/${imageId}`),
    onSuccess: invalidate,
  });

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      // Sequential keeps the "first image becomes primary" rule predictable.
      for (const file of files) {
        const url = await uploadProductImage(productId, file);
        await register.mutateAsync(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : getApiError(err).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="rounded-md border border-rule bg-paper-2/40 p-3 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium">
        Manage images
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded overflow-hidden border border-rule">
              <img
                src={img.url}
                alt={img.alt ?? ''}
                className="w-full aspect-square object-cover"
              />
              {img.isPrimary && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-accent text-white rounded">
                  Primary
                </span>
              )}
              <button
                type="button"
                onClick={() => remove.mutate(img.id)}
                disabled={remove.isPending}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ink/60 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-xs leading-none hover:bg-negative"
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/avif,image/gif,image/svg+xml"
          onChange={onPick}
          disabled={uploading}
          className="block text-sm text-ink-2 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent file:text-white file:cursor-pointer hover:file:bg-accent-2 file:transition"
        />
        {uploading && <div className="text-xs text-ink-3 mt-2">Uploading…</div>}
        {error && <div className="text-xs text-negative mt-2">{error}</div>}
        {!uploading && !error && product.isLoading && (
          <div className="text-xs text-ink-3 mt-2">Loading…</div>
        )}
        {!uploading && !error && !product.isLoading && images.length === 0 && (
          <div className="text-xs text-ink-3 mt-2 italic">
            No images yet — the first one you add becomes the primary.
          </div>
        )}
      </div>
    </div>
  );
}
